import { ReactElement, useEffect } from "react";
import { Anchor } from "@twilio-paste/core/anchor";
import { Box } from "@twilio-paste/core/box";
import { createBrowserHistory } from "history";

import {
  ConfirmationCardProps,
  TicketSubmissionFormValues,
  TicketSubmissionProps,
  TicketSubmissionVariantsEnum,
  ticketSubmissionViewCopy,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";

import { OneSupportCenterUngatedForm, OneSupportCenterGatedForm } from "./sub";
import ConfirmationCard from "../../components/confirmationCard";

import styles from "./TicketSubmissionView.module.css";

const TicketSubmissionView = ({
  calloutProps,
  loading = false,
  onCompletePersonalInformation = undefined,
  onSubmit,
  originUrl,
  priorityLevels,
  products,
  shouldBlockHistory = true,
  successDetails,
  variant = TicketSubmissionVariantsEnum.oneSupportCenterUngated,
  userInput = "",
  comment = "",
  followUpQuestions = [],
  product = "",
  issueArea = "",
}: TicketSubmissionProps): ReactElement | null => {
  const history = createBrowserHistory();
  let unblock: () => void;

  useEffect(() => {
    if (shouldBlockHistory) {
      /**
       * Confirm from user that they want to navigate away from the forms
       * Block navigation and register a callback that
       * fires when a navigation attempt is blocked. This only works for browser refresh and back button.
       * @see https://github.com/remix-run/history/blob/v4/docs/Blocking.md
       */
      unblock = history.block(() => {
        return "Are you sure you want to leave this page?";
      });
      // Clean-up the event listener on unmount - prevents memory leaks
      return (): void => {
        unblock();
      };
    }
  }, []);

  useEffect(() => {
    if (
      typeof unblock === "function" &&
      shouldBlockHistory &&
      successDetails?.wasSuccessful
    ) {
      // Start allowing user to go back without a confirmation
      unblock();
    }
  }, [successDetails?.wasSuccessful]);

  const handleSubmit = async (
    formData: TicketSubmissionFormValues
  ): Promise<void> => {
    await onSubmit(formData);
  };

  const renderConfirmation = (
    confirmationCardProps: ConfirmationCardProps
  ): ReactElement => (
    <Box as="article" className={styles.ticketSubmissionConfirmationContainer}>
      <ConfirmationCard {...confirmationCardProps} />

      {originUrl && (
        <Anchor href={originUrl} target="_self">
          {ticketSubmissionViewCopy.redirectButtonValue}
        </Anchor>
      )}
    </Box>
  );

  const shouldHideForm =
    successDetails &&
    successDetails.wasSuccessful !== undefined &&
    successDetails.shouldHideForm;

  return (
    <Box as="section" className={styles.pageContainer}>
      {!shouldHideForm && (
        <>
          {variant === TicketSubmissionVariantsEnum.oneSupportCenterGated &&
            !!priorityLevels && (
              <OneSupportCenterGatedForm
                loading={loading}
                calloutProps={calloutProps}
                priorityLevels={priorityLevels}
                onSubmit={handleSubmit}
                userInput={userInput}
                comment={comment}
                followUpQuestions={followUpQuestions}
                product={product}
                issueArea={issueArea}
              />
            )}

          {variant === TicketSubmissionVariantsEnum.oneSupportCenterUngated &&
            !!priorityLevels && (
              <OneSupportCenterUngatedForm
                loading={loading}
                calloutProps={calloutProps}
                userInput={userInput}
                priorityLevels={priorityLevels}
                onSubmit={handleSubmit}
                comment={comment}
                followUpQuestions={followUpQuestions}
                product={product}
                issueArea={issueArea}
              />
            )}
        </>
      )}

      {successDetails?.wasSuccessful &&
        renderConfirmation(successDetails.confirmationProps)}
    </Box>
  );
};

export default TicketSubmissionView;
